import { isQuiet, lastTouch, type Card, type Motivation } from '@hmawiw/shared';
import type { KeyboardEvent } from 'react';
import { useT } from '../../i18n';
import { useHour12 } from '../../lib/clock';
import { dueDays, formatShortDate, formatTime, hasDueTime, isLate } from '../../lib/dates';
import { formatRelative } from '../../lib/device';
import { useNow } from '../../hooks/useMedia';
import { useCards } from '../../store/cards';
import { timeLeft, useTimer, type RunningTimer } from '../../store/timer';
import { useSettings } from '../../store/settings';
import { useUi } from '../../store/ui';
import { Alarm, Bell, EnergyGlyph, Grip, Repeat, StepArrow, Timer } from '../icons';
import styles from './Card.module.css';

interface CardProps {
  card: Card;
  dimmed: boolean;
  now: number;
}

export function CardView({ card, dimmed, now }: CardProps) {
  const t = useT();
  const openSheet = useUi((s) => s.openSheet);
  const showToast = useUi((s) => s.showToast);
  const cycleEnergy = useCards((s) => s.cycleEnergy);
  const moveCard = useCards((s) => s.moveCard);
  const conflicts = useCards((s) => s.conflicts);
  const hasConflict = Object.values(conflicts).some((c) => c.cardId === card.id);
  const prev = card.motivation > 0 ? ((card.motivation - 1) as Motivation) : null;
  const columnCount = useSettings((s) => s.columnCount);
  const next = card.motivation < columnCount - 1 ? ((card.motivation + 1) as Motivation) : null;

  const nudge = (dir: 1 | -1) => {
    const to = card.motivation + dir;
    if (to < 0 || to >= columnCount) return;
    const from = card.motivation;
    void moveCard(card.id, to as Motivation, card.band, 0, { cause: 'key' });
    showToast(t.t('toast.movedTo', { column: t.column(to) }), {
      label: t.t('toast.undo'),
      run: () => {
        void moveCard(card.id, from, card.band, 0, { cause: 'undo' });
      },
    });
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openSheet(card.id);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      nudge(1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      nudge(-1);
    }
  };

  return (
    <div className={styles.slot} data-slot="">
      <div className={`${styles.reveal} ${styles.revealLeft} display`} aria-hidden="true" style={{ color: prev === null ? 'var(--muted)' : `var(--m${prev})` }}>
        {prev === null ? '' : t.column(prev)}
      </div>
      <div className={`${styles.reveal} ${styles.revealRight} display`} aria-hidden="true" style={{ color: next === null ? 'var(--muted)' : `var(--m${next})` }}>
        {next === null ? '' : t.column(next)}
      </div>
      <div
        className={`${styles.card}${dimmed ? ` ${styles.dim}` : ''}`}
        data-card={card.id}
        role="button"
        tabIndex={0}
        aria-label={card.title}
        onClick={() => openSheet(card.id)}
        onKeyDown={onKey}
      >
        <div className={styles.grip} data-grip="" aria-hidden="true" title={t.t('card.grip')}>
          <Grip />
        </div>
        <div className={styles.main}>
          <div className={styles.title}>{card.title}</div>
          {card.firstStep ? (
            <div className={styles.step}>
              <StepArrow size={12} />
              <span>{card.firstStep}</span>
            </div>
          ) : card.notes ? (
            <div className={styles.notes}>{card.notes.split('\n')[0]}</div>
          ) : null}
          <Meta card={card} now={now} hasConflict={hasConflict} />
        </div>
        <button
          type="button"
          className={styles.energy}
          data-tour="energy"
          aria-label={t.t('card.energyAria', { level: t.t(`energy.${card.energy}`) })}
          onClick={(e) => {
            e.stopPropagation();
            void cycleEnergy(card.id);
          }}
        >
          <EnergyGlyph level={card.energy} />
        </button>
      </div>
    </div>
  );
}

function Meta({ card, now, hasConflict }: { card: Card; now: number; hasConflict: boolean }) {
  const t = useT();
  const hour12 = useHour12();
  const running = useTimer((s) => (s.running?.cardId === card.id ? s.running : null));
  if (card.dueAt === null && !hasConflict && card.reminders.length === 0 && card.attempts.length === 0 && !running && !card.repeat) return null;
  const ongoing = card.repeat?.kind === 'ongoing';
  let due: { text: string; late: boolean } | null = null;
  if (card.dueAt !== null) {
    const days = dueDays(card.dueAt, now);
    const late = isLate(card.dueAt, now);
    let text: string;
    if (late) text = days < 0 ? t.tn('due.late', -days) : t.t('due.lateToday');
    else if (days === 0) text = t.t('due.today');
    else if (days === 1) text = t.t('due.tomorrow');
    else if (days < 7) text = t.t('due.inDays', { n: days });
    else text = t.t('due.on', { date: formatShortDate(card.dueAt, t.locale) });
    // The hour matters once the day is near; on a far date it only crowds the badge.
    if (!late && days <= 1 && hasDueTime(card.dueAt)) text += ` ${formatTime(card.dueAt, t.locale, hour12)}`;
    due = { text, late };
  }
  return (
    <div className={styles.meta}>
      {running ? <Countdown running={running} /> : null}
      {due ? <span className={`${styles.due}${due.late ? ` ${styles.late}` : ''}`}>{due.text}</span> : null}
      {ongoing ? <span className={`${styles.due}${isQuiet(card, now) ? ` ${styles.late}` : ''}`}>{t.t('card.last', { when: formatRelative(lastTouch(card), now, t.locale) })}</span> : null}
      {card.repeat ? (
        <span className={styles.bell} title={t.t('card.repeats')} aria-label={t.t('card.repeats')}>
          <Repeat size={12} />
        </span>
      ) : null}
      {card.reminders.length ? (
        <span className={styles.bell} title={t.t('card.reminders', { n: card.reminders.length })} aria-label={t.t('card.reminders', { n: card.reminders.length })}>
          {card.reminders.some((r) => r.ring) ? <Alarm size={12} /> : <Bell size={12} />}
        </span>
      ) : null}
      {card.attempts.length ? (
        <span className={styles.tries} title={t.tn('card.attempts', card.attempts.length)} aria-label={t.tn('card.attempts', card.attempts.length)}>
          <Timer size={12} />
          {card.attempts.length}
        </span>
      ) : null}
      {hasConflict ? <span className={styles.tag}>{t.t('tag.editedElsewhere')}</span> : null}
    </div>
  );
}

/** Only the card being given ten minutes ticks every second. */
function Countdown({ running }: { running: RunningTimer }) {
  const t = useT();
  const now = useNow(1000);
  const left = timeLeft(running, now);
  return (
    <span className={styles.countdown} aria-label={t.t('timer.left', { time: left })} data-countdown="">
      <Timer size={12} />
      {left}
    </span>
  );
}
